import { useEffect, useRef, useState } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

export default function HomePage() {
  const [isVisible, setIsVisible] = useState<{ [key: string]: boolean }>({});
  const sectionRefs = useRef<{ [key: string]: HTMLElement | null }>({});

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    Object.keys(sectionRefs.current).forEach((key) => {
      const element = sectionRefs.current[key];
      if (element) {
        const observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              setIsVisible((prev) => ({ ...prev, [key]: true }));
            }
          },
          { threshold: 0.1 }
        );
        observer.observe(element);
        observers.push(observer);
      }
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, []);

  const scrollToOfferings = () => {
    const element = document.getElementById('offerings');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollDown = () => {
    window.scrollBy({ top: window.innerHeight, behavior: 'smooth' });
  };

  return (
    <div className="bg-sacred-moss">
      <Navigation />

      {/* HERO SECTION */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Deep Burgundy corner vignette */}
        <div className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-deep-burgundy opacity-60"></div>
        <div 
          className="absolute inset-0" 
          style={{
            background: 'radial-gradient(circle at 0% 0%, #3D0C1A 0%, transparent 50%), radial-gradient(circle at 100% 0%, #3D0C1A 0%, transparent 50%), radial-gradient(circle at 0% 100%, #3D0C1A 0%, transparent 50%), radial-gradient(circle at 100% 100%, #3D0C1A 0%, transparent 50%)'
          }}
        ></div>

        {/* Botanical corner accents */}
        <div className="absolute top-8 left-8 w-32 h-32 opacity-[0.08]">
          <svg viewBox="0 0 100 100" className="w-full h-full text-ancient-gold">
            <path
              d="M10 10 Q30 20 50 10 Q60 30 50 50 Q30 60 10 50 Q20 30 10 10"
              stroke="currentColor"
              fill="none"
              strokeWidth="0.5"
            />
            <circle cx="50" cy="50" r="3" fill="currentColor" opacity="0.3" />
          </svg>
        </div>

        <div className="relative z-10 text-center px-8 max-w-5xl mx-auto">
          {/* Logo */}
          <div className="mb-16 flex justify-center">
            <img 
              src="https://public.readdy.ai/ai/img_res/ba7ef960-0c6d-41b7-b8de-4ad297b17548.png" 
              alt="House of Selkara" 
              className="h-32 w-auto"
            />
          </div>

          {/* Hero Statement */}
          <h1 className="font-cormorant italic text-moon-ivory text-5xl md:text-6xl lg:text-7xl leading-tight mb-8">
            <span className="block">Where women become</span>
            <span className="block ml-8">embodied sovereign</span>
            <span className="block ml-16">undeniable</span>
          </h1>

          {/* Subtext */}
          <p className="font-cormorant italic text-blush-ash text-xl md:text-2xl mb-12">
            You were never too much. You were in the wrong room.
          </p>

          {/* CTA Button */}
          <button
            onClick={scrollToOfferings}
            className="inline-block border border-ancient-gold text-ancient-gold font-cormorant tracking-widest-plus px-16 py-5 hover:bg-ancient-gold hover:text-sacred-moss transition-all duration-500 cursor-pointer whitespace-nowrap"
          >
            ENTER THE HOUSE
          </button>

          {/* Scroll Indicator */}
          <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 animate-bounce cursor-pointer" onClick={scrollDown}>
            <svg className="w-8 h-8 text-ancient-gold" viewBox="0 0 24 24" fill="none">
              <path d="M12 2C11.4477 2 11 2.44772 11 3V17.5858L6.70711 13.2929C6.31658 12.9024 5.68342 12.9024 5.29289 13.2929C4.90237 13.6834 4.90237 14.3166 5.29289 14.7071L11.2929 20.7071C11.6834 21.0976 12.3166 21.0976 12.7071 20.7071L18.7071 14.7071C19.0976 14.3166 19.0976 13.6834 18.7071 13.2929C18.3166 12.9024 17.6834 12.9024 17.2929 13.2929L13 17.5858V3C13 2.44772 12.5523 2 12 2Z" fill="currentColor"/>
            </svg>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="w-full h-px bg-ancient-gold opacity-30"></div>

      {/* BRAND STATEMENT SECTION */}
      <section 
        ref={(el) => { sectionRefs.current['brand'] = el; }}
        className={`bg-moon-ivory py-32 px-8 transition-all duration-1200 ${
          isVisible['brand'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-4xl mx-auto">
          <blockquote className="font-cormorant italic text-deep-burgundy text-3xl md:text-4xl lg:text-5xl leading-relaxed text-center mb-8">
            House of Selkara is where women come to be seen at the level of their soul and called forward into the most sovereign version of themselves.
          </blockquote>
          
          <div className="w-32 h-px bg-ancient-gold mx-auto mb-6"></div>
          
          <p className="font-cormorant text-ancient-gold text-center tracking-wider">
            — Lola Selkara, Founder
          </p>
        </div>
      </section>

      {/* Divider */}
      <div className="w-full h-px bg-ancient-gold opacity-30"></div>

      {/* THE DRIFT SECTION */}
      <section 
        ref={(el) => { sectionRefs.current['drift'] = el; }}
        className={`bg-sacred-moss py-24 transition-all duration-1200 ${
          isVisible['drift'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-7xl mx-auto px-8 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            {/* Left - Image */}
            <div className="lg:col-span-2 relative">
              <div className="relative overflow-hidden">
                <img
                  src="https://public.readdy.ai/ai/img_res/edited_14ffb8b7518160bceb7056c5eb957a2e_80850366.jpg"
                  alt="Lola Selkara"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-deep-burgundy opacity-15"></div>
              </div>
            </div>

            {/* Right - Content */}
            <div className="lg:col-span-3 lg:pl-16">
              <h2 className="font-cormorant text-ancient-gold tracking-widest-plus text-2xl mb-6">
                THE DRIFT
              </h2>
              <div className="w-24 h-px bg-ancient-gold mb-8"></div>
              
              <p className="font-garamond text-moon-ivory text-lg leading-loose mb-8">
                There is a version of you that exists underneath everything life has layered on top of her. Before the world had opinions. Before she learned to shrink. She has been waiting. This is where you come back for her.
              </p>

              <button
                onClick={() => window.REACT_APP_NAVIGATE('/about')}
                className="inline-block border border-ancient-gold text-ancient-gold font-cormorant tracking-widest-plus px-12 py-4 hover:bg-ancient-gold hover:text-sacred-moss transition-all duration-500 cursor-pointer whitespace-nowrap"
              >
                LEARN MORE
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="w-full h-px bg-ancient-gold opacity-30"></div>

      {/* THREE ARCHETYPES SECTION */}
      <section 
        ref={(el) => { sectionRefs.current['archetypes'] = el; }}
        className={`bg-deep-burgundy py-24 relative overflow-hidden transition-all duration-1200 ${
          isVisible['archetypes'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        {/* Botanical corner accent */}
        <div className="absolute bottom-8 right-8 w-32 h-32 opacity-[0.08]">
          <svg viewBox="0 0 100 100" className="w-full h-full text-ancient-gold">
            <path
              d="M50 90 Q60 70 50 50 Q30 40 10 50 M50 90 Q70 80 90 90 M10 50 Q20 30 10 10"
              stroke="currentColor"
              fill="none"
              strokeWidth="0.5"
            />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-8 lg:px-16">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="font-cormorant text-ancient-gold tracking-widest-plus text-3xl mb-4">
              THE HOUSE
            </h2>
            <p className="font-cormorant text-moon-ivory italic text-xl">
              Three forces. One return.
            </p>
          </div>

          {/* Three Columns */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-16">
            {/* The High Priestess */}
            <div className="text-left">
              <div className="mb-6">
                <svg className="w-12 h-12 text-ancient-gold" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C11.45 2 11 2.45 11 3C11 3.55 11.45 4 12 4C12.55 4 13 3.55 13 3C13 2.45 12.55 2 12 2M12 6C8.69 6 6 8.69 6 12C6 15.31 8.69 18 12 18C15.31 18 18 15.31 18 12C18 8.69 15.31 6 12 6M12 8C14.21 8 16 9.79 16 12C16 14.21 14.21 16 12 16C9.79 16 8 14.21 8 12C8 9.79 9.79 8 12 8Z"/>
                </svg>
              </div>
              <h3 className="font-cormorant text-ancient-gold tracking-widest-plus text-xl mb-4">
                THE HIGH PRIESTESS
              </h3>
              <p className="font-cormorant text-moon-ivory italic text-base mb-4">
                She sees what others cannot
              </p>
              <p className="font-garamond text-blush-ash text-sm leading-relaxed">
                The diagnostic gift. She reads between the lines of what women say and hears what they cannot yet name. She sees the pattern before it becomes the problem. She knows where the work actually is.
              </p>
            </div>

            {/* The Alchemist */}
            <div className="text-left">
              <div className="mb-6">
                <svg className="w-12 h-12 text-ancient-gold" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C11.45 2 11 2.45 11 3C11 3.55 11.45 4 12 4C12.55 4 13 3.55 13 3C13 2.45 12.55 2 12 2M12 6C8.69 6 6 8.69 6 12C6 15.31 8.69 18 12 18C15.31 18 18 15.31 18 12C18 8.69 15.31 6 12 6M12 8C14.21 8 16 9.79 16 12C16 14.21 14.21 16 12 16C9.79 16 8 14.21 8 12C8 9.79 9.79 8 12 8Z"/>
                </svg>
              </div>
              <h3 className="font-cormorant text-ancient-gold tracking-widest-plus text-xl mb-4">
                THE ALCHEMIST
              </h3>
              <p className="font-cormorant text-moon-ivory italic text-base mb-4">
                She transforms what is hidden
              </p>
              <p className="font-garamond text-blush-ash text-sm leading-relaxed">
                She brings what is buried into the light. She takes the shame, the rage, the grief — everything women have been taught to hide — and turns it into power. She does not fix. She transmutes.
              </p>
            </div>

            {/* The Architect */}
            <div className="text-left">
              <div className="mb-6">
                <svg className="w-12 h-12 text-ancient-gold" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C11.45 2 11 2.45 11 3C11 3.55 11.45 4 12 4C12.55 4 13 3.55 13 3C13 2.45 12.55 2 12 2M12 6C8.69 6 6 8.69 6 12C6 15.31 8.69 18 12 18C15.31 18 18 15.31 18 12C18 8.69 15.31 6 12 6M12 8C14.21 8 16 9.79 16 12C16 14.21 14.21 16 12 16C9.79 16 8 14.21 8 12C8 9.79 9.79 8 12 8Z"/>
                </svg>
              </div>
              <h3 className="font-cormorant text-ancient-gold tracking-widest-plus text-xl mb-4">
                THE ARCHITECT
              </h3>
              <p className="font-cormorant text-moon-ivory italic text-base mb-4">
                She builds what lasts
              </p>
              <p className="font-garamond text-blush-ash text-sm leading-relaxed">
                She constructs something that holds women's trust for generations. Not a quick fix. Not a trend. A foundation. A lineage. A house that women return to again and again because it was built for their becoming.
              </p>
            </div>
          </div>

          {/* Bottom tagline */}
          <div className="text-center">
            <p className="font-cormorant text-ancient-gold tracking-widest-plus text-lg">
              DEPTH <span className="text-2xl mx-4">·</span> DEVOTION <span className="text-2xl mx-4">·</span> DESTINY
            </p>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="w-full h-px bg-ancient-gold opacity-30"></div>

      {/* OFFERINGS PREVIEW SECTION */}
      <section 
        id="offerings"
        ref={(el) => { sectionRefs.current['offerings'] = el; }}
        className={`bg-moon-ivory py-24 transition-all duration-1200 ${
          isVisible['offerings'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-7xl mx-auto px-8 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* The Return */}
            <div className="bg-sacred-moss border border-ancient-gold p-10 group hover:bg-ancient-gold transition-all duration-500 cursor-pointer">
              <div className="flex justify-between items-start mb-6">
                <h3 className="font-cormorant text-ancient-gold tracking-widest-plus text-xl group-hover:text-sacred-moss transition-colors duration-500">
                  THE RETURN
                </h3>
                <span className="font-cormorant text-ancient-gold text-sm group-hover:text-sacred-moss transition-colors duration-500">
                  $33
                </span>
              </div>
              <div className="w-16 h-px bg-ancient-gold mb-6 group-hover:bg-sacred-moss transition-colors duration-500"></div>
              
              <p className="font-garamond text-moon-ivory text-sm mb-4 group-hover:text-sacred-moss transition-colors duration-500">
                Digital guide
              </p>
              <p className="font-cormorant text-blush-ash italic text-base mb-8 leading-relaxed group-hover:text-sacred-moss transition-colors duration-500">
                She opens it performing. She closes it arriving.
              </p>

              <button className="w-full border border-ancient-gold text-ancient-gold font-cormorant tracking-wider py-3 group-hover:border-sacred-moss group-hover:text-sacred-moss transition-all duration-500 whitespace-nowrap">
                ENTER THE RETURN
              </button>
            </div>

            {/* The Gathering */}
            <div className="bg-sacred-moss border border-ancient-gold p-10 group hover:bg-ancient-gold transition-all duration-500 cursor-pointer md:transform md:-translate-y-10">
              <div className="flex justify-between items-start mb-6">
                <h3 className="font-cormorant text-ancient-gold tracking-widest-plus text-xl group-hover:text-sacred-moss transition-colors duration-500">
                  THE GATHERING
                </h3>
                <span className="font-cormorant text-ancient-gold text-sm group-hover:text-sacred-moss transition-colors duration-500">
                  Coming Soon
                </span>
              </div>
              <div className="w-16 h-px bg-ancient-gold mb-6 group-hover:bg-sacred-moss transition-colors duration-500"></div>
              
              <p className="font-garamond text-moon-ivory text-sm mb-4 group-hover:text-sacred-moss transition-colors duration-500">
                Live community experience
              </p>
              <p className="font-cormorant text-blush-ash italic text-base mb-8 leading-relaxed group-hover:text-sacred-moss transition-colors duration-500">
                A circle. A container. A room where she is finally seen.
              </p>

              <button className="w-full border border-ancient-gold text-ancient-gold font-cormorant tracking-wider py-3 group-hover:border-sacred-moss group-hover:text-sacred-moss transition-all duration-500 whitespace-nowrap">
                JOIN THE WAITLIST
              </button>
            </div>

            {/* The Immersion */}
            <div className="bg-sacred-moss border border-ancient-gold p-10 group hover:bg-ancient-gold transition-all duration-500 cursor-pointer">
              <div className="flex justify-between items-start mb-6">
                <h3 className="font-cormorant text-ancient-gold tracking-widest-plus text-xl group-hover:text-sacred-moss transition-colors duration-500">
                  THE IMMERSION
                </h3>
                <span className="font-cormorant text-ancient-gold text-sm group-hover:text-sacred-moss transition-colors duration-500">
                  By Application
                </span>
              </div>
              <div className="w-16 h-px bg-ancient-gold mb-6 group-hover:bg-sacred-moss transition-colors duration-500"></div>
              
              <p className="font-garamond text-moon-ivory text-sm mb-4 group-hover:text-sacred-moss transition-colors duration-500">
                One on one transformation
              </p>
              <p className="font-cormorant text-blush-ash italic text-base mb-8 leading-relaxed group-hover:text-sacred-moss transition-colors duration-500">
                For the woman ready to go all the way in.
              </p>

              <button className="w-full border border-ancient-gold text-ancient-gold font-cormorant tracking-wider py-3 group-hover:border-sacred-moss group-hover:text-sacred-moss transition-all duration-500 whitespace-nowrap">
                APPLY NOW
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="w-full h-px bg-ancient-gold opacity-30"></div>

      {/* EMAIL CAPTURE SECTION */}
      <section 
        id="contact"
        ref={(el) => { sectionRefs.current['email'] = el; }}
        className={`bg-deep-burgundy py-32 relative overflow-hidden transition-all duration-1200 ${
          isVisible['email'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        {/* Botanical corner accent */}
        <div className="absolute top-8 left-8 w-32 h-32 opacity-[0.08]">
          <svg viewBox="0 0 100 100" className="w-full h-full text-ancient-gold">
            <path
              d="M10 10 Q30 20 50 10 Q60 30 50 50 Q30 60 10 50 Q20 30 10 10"
              stroke="currentColor"
              fill="none"
              strokeWidth="0.5"
            />
          </svg>
        </div>

        <div className="max-w-2xl mx-auto px-8 text-center relative z-10">
          {/* Crescent Moon */}
          <div className="mb-12 flex justify-center">
            <svg className="w-20 h-20 text-ancient-gold" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C11.45 2 11 2.45 11 3C11 3.55 11.45 4 12 4C12.55 4 13 3.55 13 3C13 2.45 12.55 2 12 2M12 6C8.69 6 6 8.69 6 12C6 15.31 8.69 18 12 18C15.31 18 18 15.31 18 12C18 8.69 15.31 6 12 6M12 8C14.21 8 16 9.79 16 12C16 14.21 14.21 16 12 16C9.79 16 8 14.21 8 12C8 9.79 9.79 8 12 8Z"/>
            </svg>
          </div>

          {/* Headline */}
          <h2 className="font-cormorant italic text-moon-ivory text-3xl md:text-4xl lg:text-5xl leading-relaxed mb-12">
            <span className="block">The House is always open</span>
            <span className="block ml-10">for the woman who is ready.</span>
          </h2>

          {/* Form */}
          <form 
            id="email-capture-form"
            data-readdy-form
            action="https://readdy.ai/api/form/d6mdh1p4a3uojp5fgmtg"
            method="POST"
            className="space-y-8"
            onSubmit={(e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const formData = new FormData(form);
              const data = new URLSearchParams();
              
              formData.forEach((value, key) => {
                data.append(key, value.toString());
              });

              fetch(form.action, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: data.toString(),
              })
                .then((response) => {
                  if (response.ok) {
                    alert('Welcome to the House. Check your email.');
                    form.reset();
                  } else {
                    alert('Something went wrong. Please try again.');
                  }
                })
                .catch(() => {
                  alert('Something went wrong. Please try again.');
                });
            }}
          >
            <div>
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                required
                className="w-full max-w-md mx-auto bg-transparent border-b-2 border-ancient-gold text-moon-ivory font-garamond text-base py-3 px-0 focus:outline-none focus:border-blush-ash transition-colors duration-300 placeholder-moon-ivory/50"
              />
            </div>

            <div>
              <input
                type="email"
                name="email"
                placeholder="Your Email"
                required
                className="w-full max-w-md mx-auto bg-transparent border-b-2 border-ancient-gold text-moon-ivory font-garamond text-base py-3 px-0 focus:outline-none focus:border-blush-ash transition-colors duration-300 placeholder-moon-ivory/50"
              />
            </div>

            <button
              type="submit"
              className="w-full max-w-md mx-auto border-2 border-ancient-gold text-ancient-gold font-cormorant tracking-widest-plus py-5 hover:bg-ancient-gold hover:text-deep-burgundy transition-all duration-500 cursor-pointer whitespace-nowrap"
            >
              ENTER THE HOUSE
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}